import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { setWorkflowCustomVariables } from "../../../../actions/taskSidebar";
import { number } from "../../../../config";
import { useTeamChipPersistence } from "../../../../shared/components/ChipEditor/TeamChipPersistenceContext";
import { addProjectVariable, updateProjectVariable } from "../../../../shared/services/projectVariables.service";
import { getCompanyFunctionalArea } from "../../../Admin/admin.service";
import { generateRandomId } from "../../../TaskSidebar/TaskSidebarContent/Attachments/attachments.helper";
import { useWorkflowContext } from "../../WorkflowContextProvider";
import { resolveAdminFunctionalAreaType } from "../adminVariableLink.helper";
import { useVariablesContext } from "../context/variableContext";
import { addWorkflowVariables, checkIsVariableNameUnique, updateFormChips, updateWorkflowVariables } from "../services/workflowVariables.service";
import { convertExpressionIdsToNames } from "../utils/expressionHelper";
import { WORKFLOW_VARIABLE_LABELS, getDateFormatOptions } from "../workflowVariables.constants";

export const useSelectedVariableValues = () => {
  const { user } = useSelector((state) => state.auth);
  const { isNewWorkflowVariable, customWorkflowVariables = [], workflowDefaults } = useWorkflowContext();
  const {
    setVariableName,
    setIsPlaceholder,
    setPlaceholderText,
    setIsDefaultSelection,
    setDefaultSelection,
    setDefaultOptions,
    setIsRequired,
    setChipType,
    setIsMinLength,
    setIsMaxLength,
    setMinLength,
    setMaxLength,
    setIsDefaultAnswer,
    setDefaultValue,
    setDateFormat,
    setDateSeparator,
    setPastDates,
    setFutureDates,
    setStartDate,
    setEndDate,
    setIsStartDate,
    setIsEndDate,
    setDateFormatOptions,
    setIsDecimal,
    setOnlyPositiveNumbers,
    setOptionsView,
    setIsAutomaticallySetValue,
    setExpressionValue,
    setNumberOnly,
    setDescription,
    setIsMultiLine,
    setLineCount,
    setShowChipName,
    setShowNumberSpinner,
    setMultiSelect,
    setMultiDimension,
  } = useVariablesContext();

  /**
   * @param {Object} [loadOpts]
   * @param {Array} [loadOpts.adminPresetRows] — e.g. Team Chips `workflowDefaultsPatch.adminCustomVariables` when context defaults are not synced yet
   */
  const setSelectedVariableValues = async (workflowVariable, newVariableName, loadOpts = {}) => {
    const adminPresetRows = loadOpts.adminPresetRows ?? workflowDefaults?.adminCustomVariables;
    if (isNewWorkflowVariable) {
      setVariableName(newVariableName);
      setPastDates(true);
      setFutureDates(true);
    } else {
      setVariableName(workflowVariable?.name);
      setPastDates(workflowVariable?.attributes?.pastDates);
      setFutureDates(workflowVariable?.attributes?.futureDates);
    }
    setIsPlaceholder(workflowVariable?.attributes?.placeholder ? true : false);
    setPlaceholderText(workflowVariable?.attributes?.placeholder ?? "");
    setIsDefaultSelection(!!workflowVariable?.attributes?.default);
    workflowVariable?.attributes?.default && setDefaultSelection(workflowVariable?.attributes?.default);
    setIsRequired(workflowVariable?.attributes?.required);
    setChipType(workflowVariable?.chipType ?? WORKFLOW_VARIABLE_LABELS.REUSABLE_CHIP.toLowerCase());
    setIsMinLength(typeof workflowVariable?.attributes?.min === "number" ? true : false);
    setIsMaxLength(typeof workflowVariable?.attributes?.max === "number" ? true : false);
    setMinLength(workflowVariable?.attributes?.min);
    setMaxLength(workflowVariable?.attributes?.max);
    setIsDefaultAnswer(typeof workflowVariable?.attributes?.defaultValue === "number" || workflowVariable?.attributes?.defaultValue ? true : false);
    setDefaultValue(workflowVariable?.attributes?.defaultValue);
    setDateFormat(workflowVariable?.attributes?.dateFormat ?? null);
    setDateSeparator(workflowVariable?.attributes?.dateSeparator ?? null);
    workflowVariable?.attributes?.dateSeparator?.label &&
      setDateFormatOptions(getDateFormatOptions(workflowVariable?.attributes?.dateSeparator?.label));
    setDescription(workflowVariable?.description || "");
    setStartDate(workflowVariable?.attributes?.startDate);
    setEndDate(workflowVariable?.attributes?.endDate);
    setIsStartDate(!!workflowVariable?.attributes?.startDate);
    setIsEndDate(!!workflowVariable?.attributes?.endDate);
    setIsDecimal(workflowVariable?.attributes?.decimal);
    setOnlyPositiveNumbers(workflowVariable?.attributes?.onlyPositiveNumbers);
    setIsAutomaticallySetValue(workflowVariable?.attributes?.autoSetValue || false);
    const expressionFromApi = workflowVariable?.attributes?.expression || "";
    const expressionWithNames = expressionFromApi ? convertExpressionIdsToNames(expressionFromApi, customWorkflowVariables) : "";
    setExpressionValue(expressionWithNames);
    setOptionsView(workflowVariable?.attributes?.optionsView);
    setNumberOnly(workflowVariable?.attributes?.numberOnly || false);
    setIsMultiLine(workflowVariable?.attributes?.isMultiLine || false);
    setLineCount(workflowVariable?.attributes?.lineCount || 1);
    setShowChipName(workflowVariable?.attributes?.showChipName !== undefined ? workflowVariable?.attributes?.showChipName : true);
    setShowNumberSpinner(workflowVariable?.attributes?.showNumberSpinner || false);
    setMultiSelect(workflowVariable?.attributes?.multiSelect || false);
    setMultiDimension(workflowVariable?.attributes?.multiDimension || false);

    /** Admin functional-area dropdowns: fetch CFA only when options are not already on the variable. */
    const functionalAreaType = resolveAdminFunctionalAreaType(workflowVariable, adminPresetRows);
    const persistedOptions = workflowVariable?.attributes?.options;
    const isOnlyDefaultDropdownPlaceholder =
      Array.isArray(persistedOptions) &&
      persistedOptions.length === 1 &&
      persistedOptions[0]?.label === "Option 1" &&
      (persistedOptions[0]?.value === 1 || persistedOptions[0]?.value === "1");
    const hasPersistedOptions = Array.isArray(persistedOptions) && persistedOptions.length > 0 && !isOnlyDefaultDropdownPlaceholder;
    const isAdminFunctionalAreaDropdown =
      workflowVariable?.adminType === WORKFLOW_VARIABLE_LABELS.IS_ADMIN_FIELD &&
      functionalAreaType != null &&
      functionalAreaType !== "" &&
      user?.companyId;

    const applyDropdownOptionsAndDefault = (list) => {
      if (!list?.length) return;
      setDefaultOptions(list);
      const def = workflowVariable?.attributes?.default;
      const existingDefault =
        def &&
        list.find((o) => o === def || (o && def && (o.value === def.value || String(o?.value) === String(def?.value) || o.label === def.label)));
      setIsDefaultSelection(!!(existingDefault || def));
      setDefaultSelection(existingDefault || def || list[0]);
    };

    if (isAdminFunctionalAreaDropdown && hasPersistedOptions) {
      applyDropdownOptionsAndDefault(persistedOptions);
    } else if (isAdminFunctionalAreaDropdown && !hasPersistedOptions) {
      const selectionOptions = await getCompanyFunctionalArea(functionalAreaType, user?.companyId);
      const list = Array.isArray(selectionOptions) ? selectionOptions : selectionOptions != null ? [selectionOptions] : [];
      applyDropdownOptionsAndDefault(list);
    } else if (workflowVariable?.attributes?.options) {
      setDefaultOptions(workflowVariable?.attributes?.options ?? []);
    }
  };

  return { setSelectedVariableValues };
};

/**
 * Form-builder project chip: lock preset fields. Team Chip tab (ChipEditor): `lockPresetFields` for global / no-manage only — not when the same UI is opened as form properties (`isFormVariable`).
 */
export const useLockTeamChipPresetFields = (isFormVariable, selectedVariable) => {
  const teamChipPersist = useTeamChipPersistence();
  /** Rely on API flag; `!workflowId` is not reliable (many workflow/global chips omit workflowId on the client). */
  const isProjectChip = useMemo(() => Boolean(selectedVariable?.isProjectVariable), [selectedVariable?.isProjectVariable]);
  return useMemo(
    () => (isFormVariable && isProjectChip) || (!isFormVariable && Boolean(teamChipPersist?.lockPresetFields)),
    [isFormVariable, isProjectChip, teamChipPersist?.lockPresetFields]
  );
};

/**
 * Lock variable name / description / delete in header (form project chip, or team tab read-only when not in form-builder context).
 */
export const useLockTeamChipHeaderEdits = (isFormVariable, selectedVariable) => {
  const teamChipPersist = useTeamChipPersistence();
  const isProjectChip = useMemo(() => Boolean(selectedVariable?.isProjectVariable), [selectedVariable?.isProjectVariable]);
  return useMemo(
    () => (isFormVariable && isProjectChip) || (!isFormVariable && Boolean(teamChipPersist?.readOnly)),
    [isFormVariable, isProjectChip, teamChipPersist?.readOnly]
  );
};

export const useAdminVariableHelper = () => {
  const teamChipPersist = useTeamChipPersistence();
  const {
    isNewWorkflowVariable,
    customWorkflowVariables,
    setSelectedVariable,
    setCustomWorkflowVariables,
    setToggleVariableList,
    setIsNewWorkflowVariable,
  } = useWorkflowContext();
  const dispatch = useDispatch();
  const saveVariableForExistingWorkflow = async (payload) => {
    /** Check if ID is temporary */
    const currentId = payload?.id;
    const isTemporaryId = typeof currentId === "number";
    if (payload?.isProjectVariable) {
      const saved = await updateProjectVariable(payload);
      let result = {
        ...saved,
        isProjectVariable: true,
      };
      const updatedVariableList = customWorkflowVariables?.map((variable) => (variable.id === saved?.id ? result : variable));
      setCustomWorkflowVariables([...updatedVariableList]);
      dispatch(setWorkflowCustomVariables([...updatedVariableList]));
      saved && setSelectedVariable(result);
      return result;
    } else if (isNewWorkflowVariable || isTemporaryId) {
      delete payload.id;
      const response = await addWorkflowVariables(payload);
      setToggleVariableList(false);
      setIsNewWorkflowVariable(false);
      const savedVariable = response?.data?.data || response?.data;
      if (savedVariable) {
        setCustomWorkflowVariables([...customWorkflowVariables, savedVariable]);
        dispatch(setWorkflowCustomVariables([...customWorkflowVariables, savedVariable]));
      }
      return savedVariable;
    } else {
      const response = await updateWorkflowVariables(payload);
      const updatedVariableList = customWorkflowVariables?.map((variable) => (variable.id === response?.id ? response : variable));
      setCustomWorkflowVariables(updatedVariableList);
      dispatch(setWorkflowCustomVariables([...updatedVariableList]));
      response && setSelectedVariable(response);
      return response;
    }
  };

  const saveVariableForNewWorkflow = async (payload) => {
    if (teamChipPersist?.projectId && teamChipPersist?.readOnly) {
      return payload;
    }

    if (teamChipPersist?.projectId && !teamChipPersist?.readOnly) {
      const rawId = payload?.id != null ? String(payload.id) : "";
      const isMongoId = /^[a-f0-9]{24}$/i.test(rawId);
      const base = {
        projectId: teamChipPersist.projectId,
        name: payload.name,
        type: payload.type,
        description: payload.description ?? "",
        attributes: payload.attributes ?? null,
        adminType: payload.adminType ?? null,
        adminCustomVariables: payload.adminCustomVariables ?? null,
        variableId: payload.variableId ?? null,
      };
      let saved = null;
      if (isMongoId) {
        saved = await updateProjectVariable({ id: rawId, ...base });
      } else {
        saved = await addProjectVariable(base);
      }
      if (saved && typeof teamChipPersist.onAfterSave === "function") {
        teamChipPersist.onAfterSave(saved);
      }
      if (saved && setSelectedVariable) {
        const sid = saved._id != null ? String(saved._id) : saved.id;
        setSelectedVariable({
          id: sid,
          variableType: saved.type,
          type: saved.type,
          name: saved.name,
          description: saved.description,
          adminType: saved.adminType,
          adminCustomVariables: saved.adminCustomVariables,
          variableId: saved.variableId,
          attributes: saved.attributes,
        });
      }
      return saved;
    }

    if (isNewWorkflowVariable) {
      payload = { ...payload, id: generateRandomId() };
      setCustomWorkflowVariables([...customWorkflowVariables, payload]);
      dispatch(setWorkflowCustomVariables([...customWorkflowVariables, payload]));
      setToggleVariableList(false);
      setIsNewWorkflowVariable(false);
    } else {
      const updatedVariableList = customWorkflowVariables?.map((variable) => (variable.id === payload?.id ? payload : variable));
      setCustomWorkflowVariables([...updatedVariableList]);
      dispatch(setWorkflowCustomVariables([...updatedVariableList]));
    }
    return payload;
  };

  return { saveVariableForExistingWorkflow, saveVariableForNewWorkflow };
};

export const useUniqueVariableNames = () => {
  const { customWorkflowVariables, workflow } = useWorkflowContext();
  const { setIsDuplicateName } = useVariablesContext();

  const checkIsDuplicateName = useCallback(
    _.debounce(async (newVariableName) => {
      let isVariableNameExist;
      if (newVariableName) {
        if (workflow?.Id) isVariableNameExist = await checkIsVariableNameUnique({ variableName: newVariableName, workflowId: workflow?.Id });
        else isVariableNameExist = isNameUnique(newVariableName);
        setIsDuplicateName(!!isVariableNameExist);
      }
    }, number.FIVE_HUNDRED),
    []
  );

  const isNameUnique = (newVariableName) => {
    return customWorkflowVariables?.some((item) => item.name.toLowerCase() === newVariableName.toLowerCase());
  };

  return { checkIsDuplicateName, isNameUnique };
};

/**
 * Hook for updating form-specific chip properties
 * Sends lightweight payload with only chipId, formId, and the changing property
 */
export const useFormChipHelper = () => {
  const { formId, selectedVariable, customWorkflowVariables, setCustomWorkflowVariables, setSelectedVariable } = useWorkflowContext();
  const dispatch = useDispatch();

  /**
   * Updates a form-specific property for a chip
   * @param {string} propertyName - The name of the property being changed (e.g., 'autoSetValue', 'dateFormat')
   * @param {any} propertyValue - The new value of the property
   */
  const updateFormChipProperty = useCallback(
    async (propertyName, propertyValue) => {
      const isProjectChip = selectedVariable?.isProjectVariable;

      const payload = {
        [isProjectChip ? "projectVariableId" : "workflowVariableId"]: selectedVariable?.id,
        formId,
        [propertyName]: propertyValue,
      };

      try {
        const response = await updateFormChips(payload);
        const updatedVariableList = customWorkflowVariables?.map((variable) => (variable.id === response?.id ? response : variable));
        setCustomWorkflowVariables(updatedVariableList);
        dispatch(setWorkflowCustomVariables([...updatedVariableList]));
        // setSelectedVariable(null);
        return response;
      } catch (error) {
        console.error("Failed to update form chip property:", error);
        return null;
      }
    },
    [formId, selectedVariable?.id]
  );

  /**
   * Checks if form-specific properties should be visible
   * @returns {boolean} True if formId exists (not a new form)
   */
  const isFormSpecificVisible = useMemo(() => {
    return !!formId;
  }, [formId]);

  return {
    updateFormChipProperty,
    isFormSpecificVisible,
    formId,
  };
};
